// src/utils/improved-chunk-uploader.js
import axios from 'axios';

export class ImprovedChunkUploader {
    constructor({
        sliceUrl,
        statusUrl,
        userId,
        chunkSize = 2 * 1024 * 1024,
        pollInterval = 2000,
        maxConcurrent = 3, // 最大并发数
        retryDelay = 1000,  // 重试延迟
        maxRetries = 3      // 最大重试次数
    }) {
        Object.assign(this, {
            sliceUrl, statusUrl, userId, chunkSize, pollInterval,
            maxConcurrent, retryDelay, maxRetries
        });
        this.uploadTasks = new Map();
        this.activeUploads = 0; // 当前活跃的上传数
    }

    /**
     * 并发控制的分片上传
     * @param {File} file
     * @param {Function} onProgress
     * @param {string} uploadId
     * @param {Function} onStatusChange
     * @returns {Promise<any>}
     */
    async upload(file, onProgress, uploadId, onStatusChange) {
        const total = Math.ceil(file.size / this.chunkSize);
        const uploadTask = this.uploadTasks.get(uploadId);

        if (!uploadTask) {
            throw new Error('Upload task not found');
        }

        // 检查已上传的分片
        const uploadedChunks = await this.checkUploadedChunks(uploadTask.taskId);
        uploadedChunks.forEach(chunkIndex => {
            uploadTask.uploadedChunks.add(chunkIndex);
        });

        // 创建待上传的分片队列
        const pendingChunks = [];
        for (let index = 0; index < total; index++) {
            if (!uploadTask.uploadedChunks.has(index)) {
                pendingChunks.push(index);
            }
        }

        // 初始进度
        const initialProgress = Math.floor((uploadTask.uploadedChunks.size / total) * 100);
        onProgress(initialProgress, uploadTask.uploadedChunks.size, total);

        // 并发上传分片
        await this.uploadChunksConcurrently(file, pendingChunks, uploadTask, total, onProgress);

        // 轮询状态直到完成
        return this._pollStatus(uploadTask.taskId, onStatusChange);
    }

    /**
 * 并发上传分片。
 * 该方法负责管理并发池，但将单个分片的上传逻辑（包括重试）委托给 uploadSingleChunk。
 * 当任何一个分片最终失败时，此方法将中止整个上传过程。
 * @param {File} file 待上传的文件对象
 * @param {number[]} pendingChunks 需要上传的分片索引数组
 * @param {Object} uploadTask 当前上传任务的元数据对象
 * @param {number} total 文件总分片数
 * @param {Function} onProgress 进度更新的回调函数
 */
    async uploadChunksConcurrently(file, pendingChunks, uploadTask, total, onProgress) {
        let chunkIndex = 0; // 使用一个共享的索引来从 pendingChunks 队列中取任务

        // 这是单个工作单元的逻辑，它会持续从队列中取任务直到队列为空
        const uploadNextChunk = async () => {
            // 当还有待上传的分片时，循环继续
            while (chunkIndex < pendingChunks.length) {

                // 检查任务是否被暂停，如果是，则等待恢复
                if (uploadTask.isPaused) {
                    await this.waitForResume(uploadTask);
                }

                // 检查任务是否被取消，如果是，则抛出错误以终止
                if (uploadTask.isCancelled) {
                    throw new Error('Upload cancelled');
                }

                // 从队列中获取下一个分片的索引
                const index = pendingChunks[chunkIndex++];

                try {
                    // 等待，直到当前活跃的上传数量小于最大并发数限制
                    while (this.activeUploads >= this.maxConcurrent) {
                        await new Promise(resolve => setTimeout(resolve, 100)); // 短暂等待，避免CPU空转
                    }

                    // 开始上传，增加活跃上传计数
                    this.activeUploads++;

                    // 调用单个分片上传方法。
                    // 注意：uploadSingleChunk 内部已经包含了重试逻辑。
                    // 如果这个 await 失败了，说明该分片在多次重试后仍无法上传成功，或者遇到了不可重试的业务错误。
                    await this.uploadSingleChunk(file, index, uploadTask, total);

                    // 上传成功后，将分片索引添加到已完成集合中
                    uploadTask.uploadedChunks.add(index);

                    // 计算并回调进度
                    const progress = Math.floor((uploadTask.uploadedChunks.size / total) * 100);
                    onProgress(progress, uploadTask.uploadedChunks.size, total);

                } catch (error) {
                    // 【核心修改点】
                    // 捕获到来自 uploadSingleChunk 的最终失败错误。
                    // 我们不再将失败的分片重新加入队列 (pendingChunks.push(index) 已被移除)。
                    console.error(`分片 ${index} 上传彻底失败，将终止整个文件的上传。失败原因:`, error.message);

                    // 直接将错误向上抛出。
                    // 这将导致调用此函数的 Promise.all 立即被拒绝(reject)，从而停止所有其他正在进行的并发上传。
                    throw error;
                } finally {
                    // 无论成功还是失败，一个上传单元都已结束，减少活跃上传计数
                    this.activeUploads--;
                }
            }
        };

        // 创建一个 Promise 池，数量为最大并发数
        const concurrentTasks = [];
        // 启动 N 个并发的工作单元 (N = maxConcurrent)
        for (let i = 0; i < Math.min(this.maxConcurrent, pendingChunks.length); i++) {
            concurrentTasks.push(uploadNextChunk());
        }

        // 等待所有并发任务完成。
        // 如果其中任何一个任务（Promise）失败，Promise.all 会立即失败，并将该错误作为结果。
        await Promise.all(concurrentTasks);
    }

    /**
     * 上传单个分片（带重试机制）
     * @param {File} file 
     * @param {number} index 
     * @param {Object} uploadTask 
     * @param {number} total 
     */
    async uploadSingleChunk(file, index, uploadTask, total) {
        const start = index * this.chunkSize;
        const end = Math.min(start + this.chunkSize, file.size);
        const blob = file.slice(start, end);

        const form = new FormData();
        form.append('file', blob, file.name);
        form.append('taskId', uploadTask.taskId);
        form.append('index', index);
        form.append('total', total);
        form.append('filename', file.name);
        form.append('userId', this.userId);

        for (let attempt = 0; attempt < this.maxRetries; attempt++) {
            try {
                await axios.post(this.sliceUrl, form, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    timeout: 30000
                });
                return; // 成功上传，退出循环
            } catch (error) {
                if (error.response && error.response.data) {
                    // error.response.data 就是后端返回的 {"code": 500, "msg": "..."}
                    const backendData = error.response.data;
                    // 如果后端返回了明确的错误信息，这通常是业务错误，不应该重试
                    // 直接抛出一个包含后端信息的错误，终止重试
                    console.error(`分片 ${index} 上传失败:`, backendData.msg);
                    throw new Error(backendData.msg || '分片上传时发生未知服务器错误');
                }

                // 如果是网络错误 (error.response 不存在) 或其他未知错误，则继续重试
                if (attempt === this.maxRetries - 1) {
                    console.error(`分片 ${index} 上传失败，已达最大重试次数:`, error);
                    throw error; // 最后一次重试失败，抛出原始错误
                }

                const delay = this.retryDelay * Math.pow(2, attempt);
                console.warn(`分片 ${index} 上传失败 (可能是网络问题)，将在 ${delay}ms 后重试...`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }

    /**
     * 等待恢复上传
     * @param {Object} uploadTask 
     */
    async waitForResume(uploadTask) {
        return new Promise(resolve => {
            const checkResume = setInterval(() => {
                if (!uploadTask.isPaused || uploadTask.isCancelled) {
                    clearInterval(checkResume);
                    resolve();
                }
            }, 100);
        });
    }

    /**
     * 智能重试机制
     * @param {Function} operation 
     * @param {number} maxRetries 
     * @param {number} baseDelay 
     */
    async retryWithBackoff(operation, maxRetries = this.maxRetries, baseDelay = this.retryDelay) {
        for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
                return await operation();
            } catch (error) {
                if (attempt === maxRetries - 1) {
                    throw error;
                }

                // 指数退避 + 随机抖动
                const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }

    /**
     * 动态调整并发数
     * @param {number} successRate 成功率
     */
    adjustConcurrency(successRate) {
        if (successRate > 0.9 && this.maxConcurrent < 6) {
            this.maxConcurrent++;
            console.log(`Increased concurrency to ${this.maxConcurrent}`);
        } else if (successRate < 0.7 && this.maxConcurrent > 1) {
            this.maxConcurrent--;
            console.log(`Decreased concurrency to ${this.maxConcurrent}`);
        }
    }

    // ... 其他方法保持不变
    generateTaskId(filename, fileSize) {
        return `${this.userId}_${filename}_${fileSize}`;
    }

    async createUploadTask(filename, fileSize) {
        const taskId = this.generateTaskId(filename, fileSize);
        const uploadTask = {
            taskId,
            filename,
            fileSize,
            uploadedChunks: new Set(),
            isPaused: false,
            isCancelled: false,
            startTime: Date.now(),
            successCount: 0,
            failureCount: 0
        };
        this.uploadTasks.set(taskId, uploadTask);
        return taskId;
    }

    async checkUploadedChunks(taskId) {
        try {
            const response = await axios.get(`${this.sliceUrl}/status`, {
                params: { taskId, userId: this.userId }
            });
            return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
            console.error('Failed to check uploaded chunks:', error);
            return [];
        }
    }

    async pauseUpload(uploadId) {
        const uploadTask = this.uploadTasks.get(uploadId);
        if (uploadTask) {
            uploadTask.isPaused = true;
        }
    }

    async resumeUpload(uploadId, onProgress) {
        const uploadTask = this.uploadTasks.get(uploadId);
        if (uploadTask) {
            uploadTask.isPaused = false;
            const total = Math.ceil(uploadTask.fileSize / this.chunkSize);
            const progress = Math.floor((uploadTask.uploadedChunks.size / total) * 100);
            onProgress(progress);
        }
    }

    async cancelUpload(uploadId) {
        const uploadTask = this.uploadTasks.get(uploadId);
        if (uploadTask) {
            uploadTask.isCancelled = true;
            this.uploadTasks.delete(uploadId);
        }
    }

    // src/utils/improved-chunk-uploader.js


    async _pollStatus(taskId, onStatusChange) {
        const params = { taskId, userId: this.userId };
        let initialRetries = 3; // 【关键】为“任务不存在”的初始错误设置重试次数
        let networkRetries = 3; // 为网络等其他错误设置重试次数

        while (true) {
            try {
                const response = await axios.get(this.statusUrl, { params });
                const { data } = response;

                if (data.code === 200) {
                    // 成功查询到状态，重置计数器以备后续网络错误
                    networkRetries = 3;

                    const status = data.data.status;
                    if (onStatusChange) {
                        onStatusChange(status, {
                            origSize: data.data.origSize, compSize: data.data.compSize,
                            compUrl: data.data.compUrl, origUrl: data.data.origUrl
                        });
                    }

                    if (status === 'DONE' || status === 'FAILED') {
                        this.uploadTasks.delete(taskId);
                        return {
                            compressionSize: data.data.compUrl, originalSize: data.data.origUrl,
                            progress: data.data.progress, filename: data.data.filename,
                            origSize: data.data.origSize, compSize: data.data.compSize,
                            status: status, error: status === 'FAILED' ? data.data.errMsg : null
                        };
                    }
                }
                // 【核心容错逻辑】: 如果是“任务不存在”的特定错误，则进行重试
                else if (data.msg && data.msg.includes('任务不存在') && initialRetries > 0) {
                    console.warn(`任务 ${taskId} 尚未对查询可见，1秒后重试... (剩余 ${initialRetries - 1} 次)`);
                    initialRetries--;
                    await new Promise(r => setTimeout(r, 3000)); // 等待3秒
                    continue; // 直接开始下一次循环尝试
                }
                // 如果是其他可预见的后端错误，或者重试次数用尽，则直接失败
                else {
                    throw new Error(data.msg || '压缩任务失败');
                }

                // 正常轮询间隔
                await new Promise(r => setTimeout(r, this.pollInterval));

            } catch (error) {
                // 对网络超时等不可预见的错误进行重试
                if (networkRetries > 0) {
                    console.warn(`查询任务 ${taskId} 状态时发生网络错误，2秒后重试...`);
                    networkRetries--;
                    await new Promise(r => setTimeout(r, 2000));
                } else {
                    this.uploadTasks.delete(taskId);
                    throw error; // 重试次数用尽，最终抛出错误
                }
            }
        }
    }
}